
"use client";

import { TopicStats } from "@/lib/types";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import HelpTooltip from "@/components/ui/HelpTooltip";

interface TopicPerformanceChartProps {
    topicStats: Record<string, TopicStats>;
}

export default function TopicPerformanceChart({ topicStats }: TopicPerformanceChartProps) {
    if (!topicStats || Object.keys(topicStats).length === 0) {
        return null;
    }

    // Sort topics by name or some other metric if needed. For now, alphabetical.
    const sortedTopics = Object.entries(topicStats).sort(([a], [b]) => a.localeCompare(b));

    return (
        <Card className="col-span-1 md:col-span-2 lg:col-span-3">
            <CardHeader>
                <CardTitle className="flex items-center">
                    Topic Performance
                    <HelpTooltip content="Breakdown of your accuracy by specific topics. Use this to identify areas that need more study (red/yellow bars) versus your strong areas (green bars)." />
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-6">
                    <Accordion type="multiple" className="w-full">
                        {sortedTopics.map(([topicName, stats]) => (
                            <AccordionItem key={topicName} value={topicName}>
                                <AccordionTrigger className="hover:no-underline">
                                    <div className="flex flex-col sm:flex-row sm:items-center w-full pr-4 gap-2">
                                        <div className="flex-1 text-left font-medium">
                                            {topicName}
                                            <span className="ml-2 text-xs text-muted-foreground font-normal">
                                                ({stats.correctQuestions}/{stats.totalQuestions})
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 w-full sm:w-1/2 md:w-1/3">
                                            <Progress value={stats.percentage} className="h-2" />
                                            <Badge variant={stats.percentage >= 70 ? "default" : stats.percentage >= 50 ? "secondary" : "destructive"} className="min-w-[4rem] justify-center">
                                                {Math.round(stats.percentage)}%
                                            </Badge>
                                        </div>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent>
                                    <div className="pl-4 space-y-4 pt-2">
                                        {Object.entries(stats.subtopics).sort(([a], [b]) => a.localeCompare(b)).map(([subtopicName, subStats]) => (
                                            <div key={subtopicName} className="space-y-1">
                                                <div className="flex justify-between text-sm">
                                                    <span>{subtopicName}</span>
                                                    <span className="text-muted-foreground text-xs">
                                                        {subStats.correctQuestions}/{subStats.totalQuestions} ({Math.round(subStats.percentage)}%)
                                                    </span>
                                                </div>
                                                <Progress value={subStats.percentage} className="h-1.5" />
                                            </div>
                                        ))}
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </CardContent>
        </Card>
    );
}
